import { gql } from '@apollo/client';

export const GET_PORTFOLIOS = gql`
  query PRODUCT_SERVICE_V2_getPortfolios($request: PortfolioRequest!) {
    getPortfolios(request: $request) {
      id
      rateSegment
      name
      productLineId
      products {
        imProductId
        tag
        productLine
        productDisplayName
        rateResolver
        carrier {
          id
          name
          logo
        }
        bcDependentData
      }
      marketingTagIds
      bcVersions
      configurable
      catalogId
      displayOrder
    }
  }
`;

export const GET_PORTFOLIOS_BY_ID = gql`
  query PRODUCT_SERVICE_V2_getPortfoliosByIdWithBC(
    $cartItems: [CartItem!]!
    $lastUpdateBC: Map
    $groupId: String = null
    $cartId: String = null
  ) {
    getPortfoliosByIdWithBC(cartItems: $cartItems, lastUpdateBC: $lastUpdateBC, groupId: $groupId, cartId: $cartId) {
      id
      rateSegment
      name
      productLineId
      products {
        imProductId
        tag
        productLine
        productDisplayName
        rateResolver
        carrier {
          id
          name
          logo
        }
        bcDependentData
      }
      marketingTagIds
      bcVersions
      configurable
      catalogId
      displayOrder
    }
  }
`;

export const GET_PORTFOLIO_BY_ID = gql`
  query PRODUCT_SERVICE_V2_getPortfolioById($portfolioId: ID!, $fetchProducts: Boolean = false) {
    getPortfolioById(portfolioId: $portfolioId, fetchProducts: $fetchProducts) {
      id
      rateSegment
      name
      productLineId
      products {
        imProductId
        tag
        productLine
        productDisplayName
        rateResolver
        carrier {
          id
          name
          logo
        }
        bcDependentData
      }
      marketingTagIds
      bcVersions
      configurable
      catalogId
      displayOrder
    }
  }
`;

export const GET_CATALOG = gql`
  query PRODUCT_SERVICE_V2_getCatalog(
    $domainId: ID!
    $effectiveDate: String
    $rateSegment: RateSegment
    $state: String
  ) {
    getCatalog(domainId: $domainId, effectiveDate: $effectiveDate, rateSegment: $rateSegment, state: $state) {
      id
      rateSegment
      productLineAttributes {
        id
        displayName
        planTile
        link
        hidden
        disclaimer
        productLine
        type
        category
        properties
      }
      marketingTags {
        id
        tag
        productLines
      }
    }
  }
`;

export const GET_SHORT_PRODUCT_TEMPLATE_BY_ID = gql`
  query PRODUCT_SERVICE_V2_getProductTemplateById($id: ID!) {
    getProductTemplateById(id: $id) {
      id
      name
      internalName
      effectiveFrom
      effectiveTo
      createdOn
      createdBy {
        id
        name
      }
      productLineId
      filterBC
      checklist {
        checklistOptions {
          card
          buttonState
          completionStatus
        }
      }
      status
    }
  }
`;

export const GET_FULL_PRODUCT_TEMPLATE_BY_ID = gql`
  query PRODUCT_SERVICE_V2_getProductTemplateById($id: ID!) {
    getProductTemplateById(id: $id) {
      id
      name
      internalName
      rateSegment
      effectiveFrom
      effectiveTo
      createdOn
      createdBy {
        id
        name
      }
      productLineId
      filterBC
      checklist {
        checklistOptions {
          card
          buttonState
          completionStatus
        }
      }
      status
      formAnswers {
        policyAttributes
        customAttributes
        planAttributes
        engValues
      }
      planSummary
      maxWaiverMessage
    }
  }
`;

export const GET_PRODUCT_TEMPLATE_CHECKLIST = gql`
  query PRODUCT_SERVICE_V2_getProductTemplateById($id: ID!) {
    getProductTemplateById(id: $id) {
      id
      status
      checklist {
        checklistOptions {
          card
          buttonState
          completionStatus
        }
      }
    }
  }
`;

export const GET_PRODUCT_TEMPLATES_BY_SPONSOR_ID = gql`
  query PRODUCT_SERVICE_V2_getProductTemplatesBySponsorId(
    $sponsorId: ID = null
    $page: Int = 1
    $filter: TemplateFilter!
    $sortBy: SortBy = DATE
    $sortOrder: SortOrder = DESC
  ) {
    getProductTemplatesBySponsorId(
      sponsorId: $sponsorId
      page: $page
      filter: $filter
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      result {
        id
        name
        internalName
        rateSegment
        effectiveFrom
        effectiveTo
        createdOn
        createdBy {
          id
          name
        }
        productLineId
        filterBC
        planSummary
        formAnswers {
          policyAttributes
          customAttributes
          planAttributes
          engValues
        }
        planSummary
        checklist {
          checklistOptions {
            card
            buttonState
            completionStatus
          }
        }
        status
      }
      totalElements
      currentPage
      totalPages
    }
  }
`;

export const GET_AVAILABLE_CARRIERS = gql`
  query PRODUCT_SERVICE_V2_getAvailableCarriers(
    $state: String!
    $effectiveDate: String!
    $rateSegment: RateSegment = CUSTOM
  ) {
    getAvailableCarriers(state: $state, effectiveDate: $effectiveDate, rateSegment: $rateSegment) {
      productLineId
      carriers {
        id
        name
        logo
        sponsorId
        defaultRecipientEmail
      }
    }
  }
`;

export const GET_AVAILABLE_PRODUCTS = gql`
  query PRODUCT_SERVICE_V2_getAvailableProducts(
    $sponsorId: String
    $effectiveDate: String!
    $rateSegment: RateSegment!
    $productLineName: String!
    $state: String
  ) {
    getAvailableProducts(
      sponsorId: $sponsorId
      effectiveDate: $effectiveDate
      rateSegment: $rateSegment
      productLineName: $productLineName
      state: $state
    ) {
      imProductId
      productDisplayName
    }
  }
`;

export const GET_PRODUCT_BY_ID = gql`
  query PRODUCT_SERVICE_V2_getProductById($productId: ID!) {
    getProductById(productId: $productId) {
      imProductId
      productDisplayName
    }
  }
`;

export const CREATE_PRODUCT_TEMPLATE = gql`
  mutation PRODUCT_SERVICE_V2_createProductTemplate($template: TemplateCreationInput!) {
    createProductTemplate(template: $template) {
      id
    }
  }
`;

export const UPDATE_PRODUCT_TEMPLATE = gql`
  mutation PRODUCT_SERVICE_V2_updateProductTemplate($productTemplate: ProductTemplateInput) {
    updateProductTemplate(productTemplate: $productTemplate) {
      id
    }
  }
`;

export const COMPLETE_CHECKLIST_STEP = gql`
  mutation PRODUCT_SERVICE_V2_completeChecklistStep($id: ID!, $card: Card!) {
    completeChecklistStep(id: $id, card: $card) {
      id
    }
  }
`;

export const UNCOMPLETE_CHECKLIST_STEP = gql`
  mutation PRODUCT_SERVICE_V2_uncompleteChecklistStep($id: ID!, $card: Card!) {
    uncompleteChecklistStep(id: $id, card: $card) {
      id
      name
      internalName
      rateSegment
      effectiveFrom
      effectiveTo
      createdOn
      createdBy {
        id
        name
      }
      productLineId
      filterBC
      checklist {
        checklistOptions {
          card
          buttonState
          completionStatus
        }
      }
      status
      formAnswers {
        policyAttributes
        customAttributes
        planAttributes
        engValues
      }
      planSummary
      maxWaiverMessage
    }
  }
`;

export const DUPLICATE_PRODUCT_TEMPLATE = gql`
  mutation PRODUCT_SERVICE_V2_duplicateProductTemplate(
    $id: ID!
    $name: String!
    $internalName: String
    $groupId: String = null
  ) {
    duplicateProductTemplate(id: $id, name: $name, internalName: $internalName, groupId: $groupId) {
      id
    }
  }
`;

export const MARK_PRODUCT_TEMPLATE_AS_STOCK = gql`
  mutation PRODUCT_SERVICE_V2_markProductTemplateAsStock($id: String!) {
    markProductTemplateAsStock(id: $id) {
      id
    }
  }
`;

export const SAVE_ANSWERS = gql`
  mutation PRODUCT_SERVICE_V2_saveAnswers($id: ID!, $answers: Map!) {
    saveAnswers(id: $id, answers: $answers) {
      id
      name
      effectiveFrom
      effectiveTo
      createdOn
      createdBy {
        id
        name
      }
      productLineId
      filterBC
      checklist {
        checklistOptions {
          card
          buttonState
          completionStatus
        }
      }
      status
      formAnswers {
        policyAttributes
        customAttributes
        planAttributes
        engValues
      }
      planSummary
      maxWaiverMessage
    }
  }
`;

export const SAVE_ANSWERS_AND_COMPLETE_STEP = gql`
  mutation PRODUCT_SERVICE_V2_saveAnswersAndCompleteStep($id: ID!, $answers: Map!, $card: Card!) {
    saveAnswers(id: $id, answers: $answers) {
      id
    }
    completeChecklistStep(id: $id, card: $card) {
      id
    }
  }
`;

export const CHANGE_ARCHIVE_STATUS = gql`
  mutation PRODUCT_SERVICE_V2_changeArchiveStatus($id: ID!, $archive: Boolean = true) {
    changeArchiveStatus(id: $id, archive: $archive) {
      id
    }
  }
`;

export const GENERATE_TEMPLATE_JSON = gql`
  mutation PRODUCT_SERVICE_V2_generateTemplateJson($id: ID!) {
    generateTemplateJson(id: $id)
  }
`;

export const ADD_CUSTOM_RATES = gql`
  mutation PRODUCT_SERVICE_V2_addCustomRates(
    $cartId: String!
    $portfolioId: String!
    $bcIndex: Int!
    $formAnswers: Map!
  ) {
    addCustomRates(cartId: $cartId, portfolioId: $portfolioId, bcIndex: $bcIndex, formAnswers: $formAnswers)
  }
`;

export const ADD_COLLATERAL = gql`
  mutation PRODUCT_SERVICE_V2_addCollateral(
    $cartId: String!
    $portfolioId: String!
    $productId: String!
    $fileName: String!
    $linkName: String!
  ) {
    addCollateral(
      cartId: $cartId
      portfolioId: $portfolioId
      productId: $productId
      fileName: $fileName
      linkName: $linkName
    )
  }
`;

export const REMOVE_COLLATERAL = gql`
  mutation PRODUCT_SERVICE_V2_removeCollateral(
    $cartId: String!
    $portfolioId: String!
    $productId: String!
    $linkName: String!
  ) {
    removeCollateral(cartId: $cartId, portfolioId: $portfolioId, productId: $productId, linkName: $linkName)
  }
`;

export const WIPE_RFQ_PORTFOLIO_DATA = gql`
  mutation PRODUCT_SERVICE_V2_wipeRFQPortfolioData($cartId: String!, $portfolioId: String!) {
    wipeRFQPortfolioData(cartId: $cartId, portfolioId: $portfolioId)
  }
`;

export const REMOVE_CUSTOM_RATES = gql`
  mutation PRODUCT_SERVICE_V2_removeCustomRates($cartId: String!, $portfolioId: String!, $bcIndex: Int!) {
    removeCustomRates(cartId: $cartId, portfolioId: $portfolioId, bcIndex: $bcIndex)
  }
`;

export const COPY_RFQ_PORTFOLIO_DATA = gql`
  mutation PRODUCT_SERVICE_V2_copyRFQPortfolioData($cartId: String!, $newCartId: String!) {
    copyRFQPortfolioData(cartId: $cartId, newCartId: $newCartId)
  }
`;

export const CREATE_DYNAMIC_PORTFOLIO = gql`
  mutation PRODUCT_SERVICE_V2_createDynamicPortfolio($portfolioIds: [String!]!, $name: String) {
    createDynamicPortfolio(portfolioIds: $portfolioIds, name: $name) {
      id
    }
  }
`;

export const CREATE_DYNAMIC_PORTFOLIO_RFQ = gql`
  mutation PRODUCT_SERVICE_V2_createDynamicRFQPortfolio(
    $portfolioIds: [String!]!
    $name: String
    $cartId: String!
    $productLineId: String!
    $cartItems: [CartItem!]!
  ) {
    createDynamicRFQPortfolio(
      portfolioIds: $portfolioIds
      name: $name
      cartId: $cartId
      productLineId: $productLineId
      cartItems: $cartItems
    ) {
      id
    }
  }
`;

export const ADD_PRODUCT_TAG = gql`
  mutation PRODUCT_SERVICE_V2_addProductTag(
    $portfolioId: String!
    $productId: String!
    $tag: String!
    $periodId: String!
  ) {
    addProductTag(portfolioId: $portfolioId, productId: $productId, tag: $tag, periodId: $periodId)
  }
`;

export const REMOVE_PRODUCT_TAG = gql`
  mutation PRODUCT_SERVICE_V2_removeProductTag($portfolioId: String!, $productId: String!, $periodId: String!) {
    removeProductTag(portfolioId: $portfolioId, productId: $productId, periodId: $periodId)
  }
`;

export const PROCESS_RFQ_TEMPLATE_FILE = gql`
  mutation PRODUCT_SERVICE_V2_processRfqTemplateFile($productId: String, $name: String!, $sponsorId: String) {
    processRfqTemplateFile(productId: $productId, name: $name, sponsorId: $sponsorId) {
      id
    }
  }
`;

export const DELETE_TEMPLATE = gql`
  mutation PRODUCT_SERVICE_V2_deleteTemplate($id: String!) {
    deleteTemplate(id: $id) {
      id
    }
  }
`;

export const GET_SHELF_PRODUCT_TEMPLATES_BY_SPONSOR_ID = gql`
  query PRODUCT_SERVICE_V2_getShelfProductTemplates(
    $sponsorId: ID = null
    $page: Int = 1
    $filter: ShelfProductFilter!
    $sortBy: SortBy = DATE
    $sortOrder: SortOrder = DESC
  ) {
    getShelfProductTemplates(
      sponsorId: $sponsorId
      page: $page
      filter: $filter
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      result {
        productTemplate {
          id
          name
          internalName
          rateSegment
          effectiveFrom
          effectiveTo
          createdOn
          createdBy {
            id
            name
          }
          productLineId
          filterBC
          planSummary
          formAnswers {
            policyAttributes
            customAttributes
            planAttributes
            engValues
          }
          planSummary
          checklist {
            checklistOptions {
              card
              buttonState
              completionStatus
            }
          }
          status
        }
        product {
          imProductId
          tag
          productLine
          productDisplayName
          rateResolver
          carrier {
            id
            name
            logo
          }
          bcDependentData
        }
      }
      totalElements
      currentPage
      totalPages
    }
  }
`;

export const GET_INITIAL_SEED_PRODUCTS_BY_SPONSOR_ID = gql`
  query PRODUCT_SERVICE_V2_getInitialSeedProductTemplatesBySponsorId(
    $sponsorId: String = null
    $page: Int = 1
    $filter: InitialSeedTemplateFilter!
    $sortBy: SortBy = NAME
    $sortOrder: SortOrder = DESC
  ) {
    getInitialSeedProductTemplatesBySponsorId(
      sponsorId: $sponsorId
      page: $page
      filter: $filter
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      result {
        template {
          id
          name
          internalName
          rateSegment
          effectiveFrom
          effectiveTo
          productLineId
          filterBC
          planSummary
          status
          creationDateTime
        }
        clientGroupId
        clientGroupName
        benefitSummaryLink
        errorMessage
        groupId
        jsonLink
      }
      totalElements
      currentPage
      totalPages
    }
  }
`;

export const REMOVE_INITIAL_SEED = gql`
  mutation PRODUCT_SERVICE_V2_removeInitialSeed($id: String!) {
    removeInitialSeed(id: $id)
  }
`;

export const GET_JSONS_UPLOAD_URLS = gql`
  query PRODUCT_SERVICE_V2_getInitialSeedUploadUrls($sponsorId: String!, $fileNames: [String!]!) {
    getInitialSeedUploadUrls(sponsorId: $sponsorId, fileNames: $fileNames)
  }
`;

export const GET_BENEFIT_SUMMARY_UPLOAD_URL = gql`
  query PRODUCT_SERVICE_V2_getBenefitSummaryUploadUrl($templateId: String!, $fileName: String!) {
    getBenefitSummaryUploadUrl(templateId: $templateId, fileName: $fileName)
  }
`;

export const GET_INITIAL_SEED_JSONS_ZIP = gql`
  mutation PRODUCT_SERVICE_V2_getInitialSeedJsonsZip($sponsorId: String!) {
    getInitialSeedJsonsZip(sponsorId: $sponsorId)
  }
`;

export const REMOVE_CUSTOM_ANSWER = gql`
  mutation PRODUCT_SERVICE_V2_removeCustomAnswer($id: ID!, $attributeId: String!) {
    removeCustomAnswer(id: $id, attributeId: $attributeId)
  }
`;

export const REMOVE_BENEFIT_SUMMARY = gql`
  mutation PRODUCT_SERVICE_V2_removeBenefitSummary($templateId: String!) {
    removeBenefitSummary(templateId: $templateId)
  }
`;

export const SAVE_REQUIRED_ANSWERS = gql`
  mutation PRODUCT_SERVICE_V2_saveRequiredAnswers($id: ID!, $attributeId: String!, $answers: Map!) {
    saveRequiredAnswers(id: $id, attributeId: $attributeId, answers: $answers) {
      id
      name
      internalName
      rateSegment
      effectiveFrom
      effectiveTo
      createdOn
      createdBy {
        id
        name
      }
      productLineId
      filterBC
      checklist {
        checklistOptions {
          card
          buttonState
          completionStatus
        }
      }
      status
      formAnswers {
        policyAttributes
        customAttributes
        planAttributes
        engValues
      }
      planSummary
      maxWaiverMessage
    }
  }
`;

export const SAVE_CUSTOM_ANSWERS = gql`
  mutation PRODUCT_SERVICE_V2_saveCustomAnswers($id: ID!, $answers: Map!) {
    saveCustomAnswers(id: $id, answers: $answers) {
      id
      name
      internalName
      rateSegment
      effectiveFrom
      effectiveTo
      createdOn
      createdBy {
        id
        name
      }
      productLineId
      filterBC
      checklist {
        checklistOptions {
          card
          buttonState
          completionStatus
        }
      }
      status
      formAnswers {
        policyAttributes
        customAttributes
        planAttributes
        engValues
      }
      planSummary
      maxWaiverMessage
    }
  }
`;

export const OVERRIDE_RATES = gql`
  mutation PRODUCT_SERVICE_V2_overrideRates($cartId: String!, $ratesOverride: [RatesOverride!]!) {
    overrideRates(cartId: $cartId, ratesOverride: $ratesOverride)
  }
`;
